/**
 * Cliente HTTP das fotos do signatário (E80.F1, ADR-0079) — raia eletrônica.
 *
 * Espelha apps/api/types/fotos_do_signatario.py e
 * apps/api/runtime/fotos_do_signatario.py. Dois grupos por caso
 * (`documento_identidade` e `ccb`), cada um com várias fotos e **uma escolhida**
 * — trocar a escolhida é um clique e não apaga nada.
 *
 * O recorte feito na tela é GEOMETRIA: a caixa e o giro viajam para o servidor,
 * e o arquivo original continua intacto lá (§2.2). Por isso existe a versão
 * `original` do blob: é ela que permite conferir se o recorte foi honesto.
 */
import type { AxiosInstance } from 'axios';

import { classifyExameEletronicoError } from '@/lib/exame_eletronico';

/** Onde a foto foi encontrada, na linguagem do dossiê. */
export type GrupoDaFoto = 'documento_identidade' | 'ccb';

/** Como a imagem efetiva foi produzida. */
export type OrigemDaFoto = 'anexo_direto' | 'recorte_na_tela';

export type RotacaoDaFoto = 0 | 90 | 180 | 270;

export const GRUPOS_DA_FOTO: readonly GrupoDaFoto[] = ['documento_identidade', 'ccb'];

export interface CaixaDoRecorte {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FotoDoSignatarioData {
  id: string;
  case_id: string;
  usuario_id: string;
  grupo: GrupoDaFoto;
  eleita: boolean;
  nome_arquivo: string;
  mime_type: string;
  size_bytes: number;
  content_sha256: string;
  bounding_box: CaixaDoRecorte | null;
  rotacao_deg: RotacaoDaFoto;
  origem: OrigemDaFoto;
  created_at: string;
}

export interface GrupoDeFotosData {
  grupo: GrupoDaFoto;
  fotos: FotoDoSignatarioData[];
  /** A foto que entra na conferência; `null` enquanto ninguém escolheu. */
  eleita_id: string | null;
}

export interface FotosDoSignatarioData {
  case_id: string;
  grupos: GrupoDeFotosData[];
}

export interface RecorteInput {
  /** `null` desfaz o recorte e devolve a foto inteira. */
  bounding_box: CaixaDoRecorte | null;
  rotacao_deg: RotacaoDaFoto;
}

function basePath(caseId: string): string {
  return `/casos/${caseId}/fotos-do-signatario`;
}

/**
 * Resposta que não é o contrato vira erro, e não um objeto meio-vazio.
 *
 * Sem esta guarda, um corpo inesperado (um 204 sem corpo, uma resposta de outra
 * rota) chegava à tela como `data` sem `grupos`, e o `.map` derrubava a PÁGINA
 * inteira — a seção das fotos levava junto o protocolo e a tabela de peças.
 * Falha de leitura tem de caber dentro da própria seção.
 */
function ehContrato(data: unknown): data is FotosDoSignatarioData {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { grupos?: unknown }).grupos)
  );
}

export async function listarFotosDoSignatario(
  client: AxiosInstance,
  caseId: string,
): Promise<FotosDoSignatarioData> {
  let data: unknown;
  try {
    ({ data } = await client.get<FotosDoSignatarioData>(basePath(caseId)));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehContrato(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}

/** Anexa uma foto ao grupo. A primeira de um grupo vazio já vem escolhida. */
export async function anexarFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  grupo: GrupoDaFoto,
  arquivo: File,
): Promise<FotoDoSignatarioData> {
  const form = new FormData();
  form.append('file', arquivo);
  form.append('grupo', grupo);
  try {
    const { data } = await client.post<FotoDoSignatarioData>(basePath(caseId), form);
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** Troca a foto do grupo que entra na conferência. Nada é apagado. */
export async function elegerFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  fotoId: string,
): Promise<FotoDoSignatarioData> {
  try {
    const { data } = await client.post<FotoDoSignatarioData>(
      `${basePath(caseId)}/${fotoId}/eleger`,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** Grava a caixa e o giro do recorte; o arquivo original não é tocado. */
export async function recortarFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  fotoId: string,
  recorte: RecorteInput,
): Promise<FotoDoSignatarioData> {
  try {
    const { data } = await client.patch<FotoDoSignatarioData>(
      `${basePath(caseId)}/${fotoId}/recorte`,
      recorte,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

export async function removerFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  fotoId: string,
): Promise<void> {
  try {
    await client.delete(`${basePath(caseId)}/${fotoId}`);
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Baixa os bytes da foto para exibir na tela.
 *
 * `versao: 'original'` traz exatamente o arquivo que subiu — é o que o editor
 * de recorte precisa, porque desenhar a caixa sobre a imagem já recortada
 * mediria a caixa contra o recorte anterior, e não contra a foto.
 */
export async function baixarFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  fotoId: string,
  versao: 'efetiva' | 'original' = 'efetiva',
): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>(`${basePath(caseId)}/${fotoId}/blob`, {
      params: { versao },
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}
